<template>
  <div class="container-fluid">
    <Breadcrumbs title="Administra tus dispositivos" main="Mi Perfil" />
    <div class="row">
      <div class="col-xl-9 col-12">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
          <p class="dispositivos-intro mb-0">
            Iniciaste sesión en estos dispositivos en los últimos {{ dias }} días.
          </p>
          <button v-if="otrasActivas > 0" type="button" class="btn btn-pill btn-cerrar-otras" :disabled="procesando"
            @click="cerrarOtras">
            <vue-feather type="log-out" size="16" />
            Cerrar las demás sesiones ({{ otrasActivas }})
          </button>
        </div>

        <div v-if="cargando" class="text-center py-5 text-muted">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Cargando dispositivos...
        </div>

        <template v-else>
          <div v-if="!tieneSesionActual" class="alert alert-light border small">
            Tu sesión actual se inició antes de activar este registro. Aparecerá aquí la próxima vez que inicies sesión.
          </div>

          <div v-if="!grupos.length" class="card dispositivo-grupo">
            <div class="card-body text-center text-muted py-5">No hay sesiones registradas.</div>
          </div>

          <div v-for="grupo in grupos" :key="grupo.clave" class="card dispositivo-grupo">
            <div class="card-body p-0">
              <div class="row g-0">
                <div class="col-md-5 grupo-resumen">
                  <div class="grupo-icono">
                    <vue-feather :type="grupo.icono" size="30" />
                  </div>
                  <div class="grupo-titulo">{{ grupo.titulo }}</div>
                </div>
                <div class="col-md-7">
                  <div v-for="sesion in grupo.sesiones" :key="sesion.id" class="sesion-item">
                    <div class="sesion-datos">
                      <div class="sesion-sistema">{{ sesion.sistema }}</div>
                      <div>{{ sesion.navegador }}</div>
                      <div v-if="sesion.ip">IP: {{ sesion.ip }}</div>
                      <div>Inicio de sesión: {{ fechaLarga(sesion.creada) }}</div>
                      <div v-if="!sesion.esActual">Última actividad: {{ tiempoRelativo(sesion.ultimaActividad) }}</div>
                      <div class="sesion-estado" :class="claseEstado(sesion)">
                        <vue-feather :type="iconoEstado(sesion)" size="16" />
                        {{ textoEstado(sesion) }}
                      </div>
                    </div>
                    <button v-if="sesion.activa && !sesion.esActual" type="button" class="btn btn-cerrar-sesion"
                      :disabled="procesando" @click="cerrarSesion(sesion)">
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <router-link to="/dashboard/mi-perfil" class="btn btn-outline-primary d-inline-flex align-items-center gap-2">
          <vue-feather type="arrow-left" size="16" />
          Volver a Mi Perfil
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";

const ICONOS = { computadora: "monitor", movil: "smartphone", tablet: "tablet" };

function tituloGrupo(tipo, sistema, n) {
  const sesiones = `${n} ${n === 1 ? "sesión" : "sesiones"}`;
  if (sistema === "iPhone" || sistema === "iPad") return `${sesiones} en ${sistema}`;
  if (sistema === "Android") return `${sesiones} en ${tipo === "tablet" ? "tablets" : "teléfonos"} Android`;
  const equipos = n === 1 ? "computadora" : "computadoras";
  if (sistema === "Desconocido") return `${sesiones} en otros dispositivos`;
  return `${sesiones} en ${equipos} con ${sistema}`;
}

export default {
  name: "MisDispositivos",
  data() {
    return {
      sesiones: [],
      dias: 30,
      cargando: true,
      procesando: false,
    };
  },
  computed: {
    tieneSesionActual() {
      return this.sesiones.some((s) => s.esActual);
    },
    otrasActivas() {
      return this.sesiones.filter((s) => s.activa && !s.esActual).length;
    },
    grupos() {
      const mapa = new Map();
      for (const s of this.sesiones) {
        const clave = `${s.tipo}|${s.sistema}`;
        if (!mapa.has(clave)) mapa.set(clave, { clave, tipo: s.tipo, sistema: s.sistema, sesiones: [] });
        mapa.get(clave).sesiones.push(s);
      }
      const grupos = [...mapa.values()].map((g) => {
        g.sesiones.sort((a, b) => Number(b.esActual) - Number(a.esActual) || Number(b.activa) - Number(a.activa));
        return {
          ...g,
          icono: ICONOS[g.tipo] || "monitor",
          titulo: tituloGrupo(g.tipo, g.sistema, g.sesiones.length),
          tieneActual: g.sesiones.some((s) => s.esActual),
        };
      });
      return grupos.sort((a, b) => Number(b.tieneActual) - Number(a.tieneActual));
    },
  },
  created() {
    this.cargar();
  },
  methods: {
    async cargar() {
      this.cargando = true;
      try {
        const { sesiones, dias } = await this.$store.dispatch("autenticacion/consultarSesiones");
        this.sesiones = sesiones || [];
        if (dias) this.dias = dias;
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: e.msg || "No se pudieron cargar tus dispositivos." });
      } finally {
        this.cargando = false;
      }
    },
    fechaLarga(valor) {
      if (!valor) return "—";
      return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(valor));
    },
    tiempoRelativo(valor) {
      if (!valor) return "—";
      const minutos = Math.floor((Date.now() - new Date(valor).getTime()) / 60000);
      if (minutos < 1) return "hace un momento";
      if (minutos < 60) return `hace ${minutos} min`;
      const horas = Math.floor(minutos / 60);
      if (horas < 24) return `hace ${horas} h`;
      return this.fechaLarga(valor);
    },
    textoEstado(sesion) {
      if (sesion.esActual) return "Tu sesión actual";
      if (sesion.activa) return "Sesión iniciada";
      return {
        logout: "Saliste de la cuenta",
        revocada: "Sesión cerrada desde otro dispositivo",
        contrasena: "Sesión cerrada por cambio de contraseña",
        expirada: "La sesión expiró",
      }[sesion.motivo] || "Sesión finalizada";
    },
    iconoEstado(sesion) {
      if (sesion.esActual) return "check-circle";
      if (sesion.activa) return "log-in";
      return "minus-circle";
    },
    claseEstado(sesion) {
      if (sesion.esActual) return "estado-actual";
      if (sesion.activa) return "estado-activa";
      return "estado-finalizada";
    },
    async cerrarSesion(sesion) {
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Cerrar sesión en este dispositivo",
        text: `${sesion.sistema} · ${sesion.navegador}. Tendrá que volver a iniciar sesión en ese dispositivo.`,
        showCancelButton: true,
        confirmButtonText: "Cerrar sesión",
        cancelButtonText: "Cancelar",
      });
      if (!isConfirmed) return;
      await this.ejecutar(() => this.$store.dispatch("autenticacion/cerrarSesionDispositivo", { id: sesion.id }));
    },
    async cerrarOtras() {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "Cerrar las demás sesiones",
        text: `Se cerrará tu sesión en ${this.otrasActivas} dispositivo(s). Este dispositivo seguirá con la sesión iniciada.`,
        showCancelButton: true,
        confirmButtonText: "Cerrar sesiones",
        cancelButtonText: "Cancelar",
      });
      if (!isConfirmed) return;
      await this.ejecutar(() => this.$store.dispatch("autenticacion/cerrarOtrasSesiones"));
    },
    async ejecutar(accion) {
      this.procesando = true;
      try {
        const data = await accion();
        Swal.fire({ icon: "success", title: data?.msg || "Sesión cerrada", timer: 1800, showConfirmButton: false });
        await this.cargar();
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: e.msg || "No se pudo cerrar la sesión." });
      } finally {
        this.procesando = false;
      }
    },
  },
};
</script>

<style scoped>
.dispositivos-intro {
  max-width: 560px;
  font-size: 14px;
  line-height: 1.6;
}

.dispositivo-grupo {
  border: 1px solid #e6e6ef;
  border-radius: 12px;
  box-shadow: none;
  margin-bottom: 24px;
}

.grupo-resumen {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
}

.grupo-icono {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #eef0ff;
  color: #5c61f2;
}

.grupo-titulo {
  padding-top: 18px;
  font-size: 16px;
  font-weight: 600;
}

.sesion-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  font-size: 13px;
  line-height: 1.6;
}

.sesion-item + .sesion-item {
  border-top: 1px solid #e6e6ef;
}

.sesion-sistema {
  font-size: 14px;
  font-weight: 600;
}

.sesion-estado {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-weight: 600;
}

.estado-actual {
  color: #5c61f2;
}

.estado-activa {
  color: #10b981;
}

.estado-finalizada {
  color: #8a8a99;
}

.btn-cerrar-sesion {
  flex-shrink: 0;
  padding: 6px 16px;
  border: 1px solid #e11d48;
  border-radius: 30px;
  background: transparent;
  color: #e11d48;
  font-size: 13px;
  font-weight: 600;
}

.btn-cerrar-sesion:hover:not(:disabled) {
  background-color: #e11d48;
  color: #fff;
}

.btn-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 20px;
  border: none;
  border-radius: 30px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.btn-cerrar-otras {
  background-color: #e11d48;
}

.btn-cerrar-otras:hover:not(:disabled) {
  background-color: #be123c;
  color: #fff;
}

.btn-pill:disabled {
  color: #fff;
  opacity: 0.65;
}

@media (min-width: 768px) {
  .col-md-7 {
    border-left: 1px solid #e6e6ef;
  }
}
</style>
